import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as PhosphorIcons from "@phosphor-icons/react";
import { CaretLeft, CaretRight, FloppyDisk, FilePdf, FileDoc, DownloadSimple } from "@phosphor-icons/react";
import { useSync } from "../context/SyncContext";
import SyncBadge from "../components/SyncBadge";
import Field from "../components/Field";
import { getInspection, saveInspection, metaStore } from "../lib/db";
import { pushInspection } from "../lib/sync";
import { fetchWeatherAt } from "../lib/geo";
import { api, API_BASE } from "../lib/api";

function SectionIcon({ name, ...props }) {
  const Icon = PhosphorIcons[name] || PhosphorIcons.Circle;
  return <Icon {...props} />;
}

function sectionHasData(section, inspection) {
  return section.items.some((item) => {
    if (item.type === "info") return false;
    if (item.type === "photos") return (inspection.photos || []).length > 0;
    const v = inspection.data?.[item.id];
    if (v == null || v === "") return false;
    if (item.type === "checkbox") return !!v.checked;
    if (item.type === "group") return Object.values(v.children || {}).some(Boolean) || v.note;
    if (item.type === "gps") return v.lat != null;
    return true;
  });
}

export default function InspectionForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const { runSync, refreshPending, online } = useSync();
  const [schema, setSchema] = useState(null);
  const [inspection, setInspection] = useState(null);
  const [current, setCurrent] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showToc, setShowToc] = useState(false);
  const timer = useRef(null);
  const topRef = useRef(null);

  useEffect(() => {
    (async () => {
      const insp = await getInspection(id);
      if (!insp) {
        setNotFound(true);
        return;
      }
      setInspection(insp);
      let sch = await metaStore.getItem("schema");
      if (navigator.onLine) {
        try {
          const { data } = await api.get("/schema");
          sch = data.sections;
          await metaStore.setItem("schema", sch);
        } catch (e) {}
      }
      setSchema(sch);
    })();
  }, [id]);

  const persist = useCallback((next) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      await saveInspection(next);
      refreshPending();
    }, 600);
  }, [refreshPending]);

  const update = useCallback(
    (mutator) => {
      setInspection((prev) => {
        const next = { ...prev, dirty: true, synced: false };
        mutator(next);
        persist(next);
        return next;
      });
    },
    [persist]
  );

  function setData(itemId, val) {
    update((n) => {
      n.data = { ...n.data, [itemId]: val };
    });
  }
  function setPhotos(val) {
    update((n) => {
      n.photos = val;
    });
  }
  function setTitle(val) {
    update((n) => {
      n.title = val;
    });
  }

  // Auto-estimate crash-time weather once GPS + crash time are both set.
  const autoWxRef = useRef(null);
  useEffect(() => {
    if (!inspection) return;
    const gps = inspection.data?.location_gps;
    const ct = inspection.data?.time_of_crash;
    if (!gps || gps.lat == null || !ct || !navigator.onLine) return;
    if (inspection.data?.weather_crash) return; // never overwrite existing
    const key = `${gps.lat.toFixed(4)},${gps.lng.toFixed(4)}|${ct}`;
    if (autoWxRef.current === key) return;
    autoWxRef.current = key;
    fetchWeatherAt(gps.lat, gps.lng, ct)
      .then((w) => {
        setData("weather_crash", w);
        toast.success("Weather at crash time auto-filled");
      })
      .catch(() => {
        autoWxRef.current = null; // allow retry on next change
      });
    // eslint-disable-next-line
  }, [inspection?.data?.location_gps, inspection?.data?.time_of_crash]);

  async function saveExit() {
    if (timer.current) clearTimeout(timer.current);
    await saveInspection(inspection);
    refreshPending();
    runSync();
    toast.success("Saved");
    nav("/");
  }

  async function exportReport(format) {
    if (!online) {
      toast.error("Export needs internet — the branded report is generated on the server. Your data is saved locally.");
      return;
    }
    setExporting(true);
    try {
      if (timer.current) clearTimeout(timer.current);
      await saveInspection(inspection);
      await pushInspection(inspection);
      await refreshPending();
      const res = await api.get(`/inspections/${id}/export`, {
        params: { format },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = (inspection.title || "crash-inspection").replace(/[^a-z0-9]+/gi, "_");
      a.download = `${safe}.${format === "docx" ? "docx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported`);
    } catch (e) {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  }

  function goTo(i) {
    setCurrent(i);
    setShowToc(false);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  if (notFound) {
    return (
      <div className="p-10 text-center">
        <p className="text-steel mb-4">Inspection not found.</p>
        <button className="uc-btn uc-btn-primary" onClick={() => nav("/")}>Back to list</button>
      </div>
    );
  }
  if (!inspection || !schema) {
    return <div className="p-10 text-steel">Loading inspection…</div>;
  }

  const section = schema[current];
  const isFirst = current === 0;
  const isLast = current === schema.length - 1;

  return (
    <div className="min-h-screen bg-paper pb-28">
      <span ref={topRef} />
      <header className="sticky top-0 z-30 bg-white border-b-2 border-line">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3 mb-3">
            <button
              data-testid="back-btn"
              onClick={saveExit}
              className="w-11 h-11 rounded-full border-2 border-line flex items-center justify-center text-navy shrink-0"
              aria-label="Save and back"
            >
              <CaretLeft size={22} weight="bold" />
            </button>
            <input
              className="flex-1 h-11 px-3 rounded-lg border-2 border-line font-head font-semibold text-navydark focus:outline-none focus:border-teal"
              placeholder="Inspection title / reference"
              data-testid="title-input"
              value={inspection.title || ""}
              onChange={(e) => setTitle(e.target.value)}
            />
            <SyncBadge />
          </div>
          {/* Section tab bar */}
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
            {schema.map((s, i) => {
              const done = sectionHasData(s, inspection);
              const active = i === current;
              return (
                <button
                  key={s.id}
                  data-testid={`section-tab-${s.id}`}
                  onClick={() => goTo(i)}
                  className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm font-head font-semibold border-2 transition-colors ${
                    active
                      ? "bg-navy text-white border-navy"
                      : done
                      ? "bg-tealsoft text-navy border-teal"
                      : "bg-white text-steel border-line"
                  }`}
                >
                  <span className="w-5 h-5 rounded-full bg-black/10 flex items-center justify-center text-[11px]">
                    {i + 1}
                  </span>
                  {s.title}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 uc-fade" key={section.id}>
        <div className="flex items-start gap-3 mb-1">
          <div className="w-11 h-11 rounded-xl bg-navy text-white flex items-center justify-center shrink-0">
            <SectionIcon name={section.icon} size={24} weight="bold" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-teal">
              Section {current + 1} of {schema.length}
            </p>
            <h2 className="font-head text-2xl font-bold text-navydark tracking-tight leading-tight">
              {section.title}
            </h2>
          </div>
        </div>
        {section.description && <p className="text-steel text-sm mb-6 ml-14">{section.description}</p>}

        <div className="space-y-6">
          {section.items.map((item) => (
            <div key={item.id} data-testid={`item-wrap-${item.id}`}>
              <Field
                item={item}
                allData={inspection.data}
                value={item.type === "photos" ? inspection.photos : inspection.data?.[item.id]}
                onChange={(v) => (item.type === "photos" ? setPhotos(v) : setData(item.id, v))}
              />
            </div>
          ))}
        </div>

        {isLast && (
          <div className="mt-8 p-5 rounded-2xl bg-white border-2 border-line">
            <p className="font-head font-semibold text-navydark mb-1">Export report</p>
            <p className="text-sm text-steel mb-4">
              Generate a branded Urban Connection report including your photos.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="export-pdf-btn"
                disabled={exporting}
                onClick={() => exportReport("pdf")}
                className="uc-btn uc-btn-primary"
              >
                <FilePdf size={20} weight="bold" /> PDF
              </button>
              <button
                data-testid="export-docx-btn"
                disabled={exporting}
                onClick={() => exportReport("docx")}
                className="uc-btn uc-btn-teal"
              >
                <FileDoc size={20} weight="bold" /> Word
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Sticky bottom nav */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white border-t-2 border-line">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="prev-btn"
            disabled={isFirst}
            onClick={() => goTo(current - 1)}
            className="uc-btn uc-btn-ghost flex-1 disabled:opacity-40"
          >
            <CaretLeft size={20} weight="bold" /> Prev
          </button>
          <button
            data-testid="save-btn"
            onClick={saveExit}
            className="uc-btn uc-btn-ghost !px-4"
            aria-label="Save and exit"
          >
            <FloppyDisk size={20} weight="bold" />
          </button>
          {isLast ? (
            <button data-testid="done-btn" onClick={saveExit} className="uc-btn uc-btn-primary flex-1">
              <FloppyDisk size={20} weight="bold" /> Done
            </button>
          ) : (
            <button
              data-testid="next-btn"
              onClick={() => goTo(current + 1)}
              className="uc-btn uc-btn-primary flex-1"
            >
              Next <CaretRight size={20} weight="bold" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
